import { Component, OnInit } from '@angular/core';
import {
  MATERIAL_IMPORTS,
  COMMON_IMPORTS,
} from '../../../shared/imports/common-imports';
import { MatDialog } from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { UserRole } from '../../../core/auth/models/profile';
import { UserData } from '../../../shared/models/user-data.model';
import { AdminService } from '../../../shared/services/admin.service';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { Region } from '../../../shared/models/region.model';
import { LambdaApiService } from '../../../shared/services/lambda-api.service';
import { AuthService } from '../../../core/auth/services/auth.service';
import { Market } from '../../../shared/models/market.model';
import { AllRegions } from '../../../shared/data/regions';

@Component({
  selector: 'app-new-user-dialog',
  standalone: true,
  imports: [MATERIAL_IMPORTS, COMMON_IMPORTS, SpinnerComponent],
  templateUrl: './new-user-dialog.component.html',
  styleUrl: './new-user-dialog.component.scss',
})
export class NewUserDialogComponent implements OnInit {
  markets: Market[] = [];
  private allMarkets: Market[] = [];
  userForm!: FormGroup;
  regions: Region[] = [];
  therapeuticAreas = ['OBU', 'BBU', 'RDU'];
  userRoles = [
    { role: UserRole.ADMIN, label: 'Admin' },
    { role: UserRole.TA_USER, label: 'TA User' },
    { role: UserRole.LEADERSHIP_USER, label: 'Leadership User' },
  ];
  isSubmitting = false;
  isLoading = false;
  loggedUserPrid = '';
  hideRegionData = true;
  private readonly allRegions = [...AllRegions];
  userData: { email: string; name: string; prid: string }[] = [];

  constructor(
    public dialog: MatDialog,
    private readonly _apiService: LambdaApiService,
    private readonly fb: FormBuilder,
    private readonly _adminService: AdminService,
    private readonly _authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.userForm = this.fb.group({
      email: ['', [Validators.required]],
      prid: ['', [Validators.required]],
      name: ['', [Validators.required]],
      typeOfUser: ['', Validators.required],
      region: ['', Validators.required],
      market: ['', Validators.required],
      therapeuticArea: this.fb.group(
        {
          OBU: [false],
          BBU: [false],
          RDU: [false],
        },
        { validators: this.atLeastOneCheckboxValidator },
      ),
    });
    this.userForm.disable();

    this.loadUserData();
    this.setFormValueTriggers();
    this.loadRegions();

    const profile = this._authService.getProfile();
    this.loggedUserPrid = profile ? profile.prid : '';
  }

  loadUserData() {
    this._adminService.listAllCognitoUsers().then((data: any) => {
      const existingEmails = this._adminService.cachedUserEmails;
      this.userData = data.users
        .filter((user: any) => !existingEmails.includes(user.email))
        .map((user: any) => {
          const { name, prid } = this.extractUserDataFromCognito(user);
          return {
            email: user.email,
            name,
            prid,
          };
        });
    });
  }

  setFormValueTriggers() {
    //form region change sets markets
    this.userForm.get('region')?.valueChanges.subscribe((region: string) => {
      if (!region) {
        return;
      }

      if (this.userForm.get('typeOfUser')?.value !== UserRole.TA_USER) {
        this.userForm.get('market')?.setValue(['_ALL']);
        return;
      }

      const regionId = region;
      const filteredMarkets = this.allMarkets
        .filter((market) => market.region === regionId)
        .sort((a, b) => a.name.localeCompare(b.name));
      this.userForm.get('market')?.enable();
      this.userForm.get('market')?.setValue([]);
      this.userForm.get('market')?.markAsUntouched();
      this.markets = filteredMarkets;
    });

    //form email change sets name and prid from userData
    this.userForm.get('email')?.valueChanges.subscribe((email: string) => {
      if (!email) {
        this.userForm.get('name')?.setValue('');
        this.userForm.get('prid')?.setValue('');
        return;
      }

      const selectedUser = this.userData.find((user) => user.email === email);
      if (selectedUser) {
        this.userForm.get('name')?.setValue(selectedUser.name);
        this.userForm.get('prid')?.setValue(selectedUser.prid);
      }
    });

    //form typeOfUser change toggles region and market
    this.userForm
      .get('typeOfUser')
      ?.valueChanges.subscribe((role: UserRole) => {
        this.hideRegionData = role !== UserRole.TA_USER;

        if (this.hideRegionData) {
          this.userForm.get('market')?.enable();
          this.userForm.get('region')?.setValue(this.allRegions);
          this.userForm.get('market')?.setValue(['_ALL']);
          // Set all therapeutic areas to true
          this.therapeuticAreas.forEach((area) => {
            this.getTherapeuticAreaFormControl(area).setValue(true);
          });
        } else {
          this.userForm.get('market')?.disable();
          this.userForm.get('region')?.setValue('');
          this.userForm.get('market')?.setValue(['']);
          // Reset all therapeutic areas to false
          this.therapeuticAreas.forEach((area) => {
            this.getTherapeuticAreaFormControl(area).setValue(false);
          });

          // mark as untouched to trigger validation
          this.userForm.get('region')?.markAsUntouched();
          this.userForm.get('market')?.markAsUntouched();
          this.therapeuticAreaForm.markAsUntouched();
        }
      });

    //form market change when _ALL is selected toggle all markets
    this.userForm.get('market')?.valueChanges.subscribe((markets: string[]) => {
      if (this.userForm.get('typeOfUser')?.value !== UserRole.TA_USER) {
        return;
      }

      if (markets.includes('_ALL')) {
        if (this.userForm.get('market')?.value.length > 1) {
          this.userForm.get('market')?.setValue(['']);
          return;
        }

        const allMarketNames = this.markets.map((market) => market.name);
        this.userForm.get('market')?.setValue(allMarketNames);
      }
    });
  }

  extractUserDataFromCognito(cognitoData: any): { prid: string; name: string } {
    const userAttributes = cognitoData.attributes as {
      name: string;
      value: string;
    }[];

    const firstName =
      userAttributes.find((attr) => attr.name === 'given_name')?.value || '';
    const lastName =
      userAttributes.find((attr) => attr.name === 'middle_name')?.value || '';
    const shortMail =
      userAttributes.find((attr) => attr.name === 'preferred_username')
        ?.value || '';
    const prid = shortMail.split('@')[0];

    const fullName = `${firstName} ${lastName}`.trim();

    return { prid, name: fullName };
  }

  loadRegions() {
    this.isLoading = true;
    this._apiService
      .getRegions()
      .then((data: Region[]) => {
        this.regions = data;

        this.loadMarkets();
      })
      .finally(() => {
        this.isLoading = false;
        this.userForm.enable();
        // disable name and prid until email is entered
        this.disableReadOnlyForms();
        this.userForm.get('market')?.disable();
      });
  }

  loadMarkets() {
    this._apiService.getMarkets().then((data: Market[]) => {
      this.markets = data.sort((a, b) => a.name.localeCompare(b.name));

      this.allMarkets = [...this.markets];
    });
  }

  disableReadOnlyForms() {
    // disable name and prid until email is entered
    this.userForm.get('name')?.disable();
    this.userForm.get('prid')?.disable();
  }

  atLeastOneCheckboxValidator(group: FormGroup) {
    const values = Object.values(group.value);
    return values.some((value: any) => value === true)
      ? null
      : { required: true };
  }

  onSubmit(): void {
    if (this.userForm.valid) {
      const formValues = this.userForm.getRawValue();
      const userData: UserData = {
        id: '', // ID would be generated by backend
        prid: formValues.prid,
        name: formValues.name,
        email: formValues.email,
        role: formValues.typeOfUser,
        regions: Array.isArray(formValues.region)
          ? formValues.region
          : [formValues.region],
        markets: formValues.market,
        therapeutic_areas: Object.keys(formValues.therapeuticArea).filter(
          (area) => formValues.therapeuticArea[area] === true,
        ),
        approvedBy: this.loggedUserPrid,
      };

      this.submitUserData(userData);
    } else {
      this.userForm.markAllAsTouched();
      this.disableReadOnlyForms();
    }
  }

  submitUserData(userData: UserData) {
    this.isSubmitting = true;
    this.userForm.disable();

    this._adminService
      .createUser(userData)
      .then(() => {
        this.closeDialog();
      })
      .catch((error) => {
        console.error('Error creating user:', error);
        this.userForm.enable();
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  get therapeuticAreaForm() {
    return this.userForm.get('therapeuticArea') as FormGroup;
  }

  getTherapeuticAreaFormControl(control: string) {
    return this.therapeuticAreaForm.get(control) as FormControl;
  }

  closeDialog() {
    this.dialog.closeAll();
  }
  getMarketDisplay(selected: string[] | null | undefined): string {
    if (!selected || selected.length === 0) {
      return '';
    }
    // Remove empty string or _ALL if present
    const filtered = selected.filter((v: string) => v && v !== '_ALL');
    if (filtered.length === 0) {
      return '';
    }
    if (filtered.length <= 2) {
      return filtered.join(', ');
    }
    const firstTwo = filtered.slice(0, 2).join(', ');
    const moreCount = filtered.length - 2;
    return `${firstTwo} +${moreCount}`;
  }
}
